import { useState, useEffect, useRef } from 'react';

interface HeroSlide {
  headline: string;
  subheadline: string;
  primaryCTA: {
    text: string;
    action: () => void;
  };
  secondaryCTA: {
    text: string;
    link: string;
  };
  image: string;
  imageAlt: string;
}

const slides: HeroSlide[] = [
  {
    headline: "Tulsa's Premier Turf Distributor",
    subheadline: "Premium synthetic turf for homes, pets, putting greens, and sports projects. Pickup or fast delivery across Tulsa and nearby cities.",
    primaryCTA: {
      text: "Request Pricing",
      action: () => {
        const contactLink = document.querySelector('a[href="tel:918-806-8873"]');
        if (contactLink) {
          contactLink.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    secondaryCTA: {
      text: "Get Free Samples",
      link: "/landscape-turf"
    },
    image: "https://public.readdy.ai/ai/img_res/b5afc3917b48b374370a04865b3d143f.jpg",
    imageAlt: "Premium Synthetic Turf Installation Tulsa Oklahoma"
  },
  {
    headline: "Contractor and Wholesale Pricing",
    subheadline: "Stocked inventory, fast turnaround, and contractor-friendly pricing for landscapers, builders, and installers in Tulsa.",
    primaryCTA: {
      text: "Contractor Signup",
      action: () => {
        const contactLink = document.querySelector('a[href="tel:918-806-8873"]');
        if (contactLink) {
          contactLink.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    },
    secondaryCTA: {
      text: "View Turf Options",
      link: "/landscape-turf"
    },
    image: "https://readdy.ai/api/search-image?query=professional%20warehouse%20interior%20with%20stacked%20rolls%20and%20pallets%20of%20premium%20synthetic%20artificial%20turf%20grass%20inventory%20clean%20organized%20commercial%20distribution%20center%20bright%20industrial%20lighting%20modern%20facility%20professional%20photography%20showing%20quality%20turf%20products%20ready%20for%20contractor%20delivery&width=1920&height=1080&seq=wholesale918hero&orientation=landscape",
    imageAlt: "Wholesale Turf Distribution Tulsa"
  },
  {
    headline: "Pet Turf That Actually Holds Up",
    subheadline: "Durable, drainable, easy-to-clean pet turf options built for Oklahoma heat and heavy use.",
    primaryCTA: {
      text: "Shop Pet Turf",
      action: () => {
        window.REACT_APP_NAVIGATE('/pet-turf');
      }
    },
    secondaryCTA: {
      text: "Talk to a Specialist",
      link: "tel:918-806-8873"
    },
    image: "https://readdy.ai/api/search-image?query=luxury%20upscale%20residential%20backyard%20with%20pristine%20green%20synthetic%20pet%20turf%20artificial%20grass%20happy%20golden%20retriever%20dog%20playing%20modern%20high%20end%20landscaping%20design%20bright%20natural%20sunlight%20premium%20outdoor%20living%20space%20professional%20real%20estate%20photography%20showing%20beautiful%20pet%20friendly%20lawn&width=1920&height=1080&seq=petturf918hero&orientation=landscape",
    imageAlt: "Premium Pet Turf Tulsa Oklahoma"
  },
  {
    headline: "Putting Greens, Dialed In",
    subheadline: "Tour-grade putting green turf with realistic roll and clean edges. Perfect for premium residential builds.",
    primaryCTA: {
      text: "Explore Putting Greens",
      action: () => {
        window.REACT_APP_NAVIGATE('/putting-green-turf');
      }
    },
    secondaryCTA: {
      text: "Book a Consult",
      link: "tel:918-806-8873"
    },
    image: "https://readdy.ai/api/search-image?query=luxury%20residential%20backyard%20custom%20putting%20green%20with%20premium%20synthetic%20golf%20turf%20artificial%20grass%20perfect%20smooth%20surface%20golf%20ball%20and%20flag%20modern%20upscale%20landscaping%20design%20beautiful%20evening%20lighting%20high%20end%20outdoor%20living%20space%20professional%20architecture%20photography%20showing%20tour%20quality%20putting%20green&width=1920&height=1080&seq=putting918hero&orientation=landscape",
    imageAlt: "Custom Putting Green Turf Tulsa"
  }
];

export default function HeroSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isHovered || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [isHovered, isPaused]);

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
    setIsPaused(true);
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false);
    }, 12000);
  };

  const handlePrevious = () => {
    const newIndex = (currentIndex - 1 + slides.length) % slides.length;
    handleDotClick(newIndex);
  };

  const handleNext = () => {
    const newIndex = (currentIndex + 1) % slides.length;
    handleDotClick(newIndex);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }
    
    if (isRightSwipe) {
      handlePrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const currentSlide = slides[currentIndex];

  return (
    <section 
      className="relative h-screen flex items-center justify-center overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Images */}
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-[650ms] ease-out ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ pointerEvents: index === currentIndex ? 'auto' : 'none' }}
          >
            <img 
              src={slide.image}
              alt={slide.imageAlt}
              className={`w-full h-full object-cover transition-transform duration-[650ms] ease-out ${
                index === currentIndex ? 'scale-100' : 'scale-[1.02]'
              }`}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-6 max-w-5xl mx-auto w-full">
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`transition-all duration-[550ms] ease-out ${
              index === currentIndex
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-3 absolute inset-0 pointer-events-none'
            }`}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              {slide.headline}
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-white/95 max-w-3xl mx-auto leading-relaxed">
              {slide.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={slide.primaryCTA.action}
                className="bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-green-700 hover:shadow-xl transition-all duration-200 whitespace-nowrap cursor-pointer inline-block"
                style={{ transform: 'translateY(0)' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {slide.primaryCTA.text}
              </button>
              <a
                href={slide.secondaryCTA.link}
                className="bg-white text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-100 hover:shadow-xl transition-all duration-200 whitespace-nowrap cursor-pointer inline-block"
                style={{ transform: 'translateY(0)' }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {slide.secondaryCTA.text}
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={handlePrevious}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all duration-200 opacity-0 hover:opacity-100 group-hover:opacity-100 cursor-pointer"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-line text-2xl text-white"></i>
      </button>
      <button
        onClick={handleNext}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full transition-all duration-200 opacity-0 hover:opacity-100 group-hover:opacity-100 cursor-pointer"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-line text-2xl text-white"></i>
      </button>

      {/* Navigation Dots */}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex items-center justify-center gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            className={`h-2 rounded-full transition-all duration-300 ease-out cursor-pointer ${
              index === currentIndex
                ? 'bg-white w-8'
                : 'bg-white/40 hover:bg-white/60 w-2'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
